import { getCssClassForUpDownEqual } from '@plato/ui-legacy/lib/color-helpers';
import { cn } from '@plato/ui/util/classes';
import type { PropsWithChildren } from 'react';

type Props = {
    className?: string;
} & PropsWithChildren;

export function DetailedChartTooltip({ children, className }: Props) {
    return (
        <div
            className={cn(
                'rounded-tremor-default border-tremor-border bg-tremor-background',
                'text-tremor-default shadow-tremor-dropdown',
                'border px-2 py-1',
                className,
            )}
        >
            <div className="flex w-full flex-col gap-0.5 text-sm font-normal text-neutral-900">
                {children}
            </div>
        </div>
    );
}

type RowProps = {
    borderTop?: boolean;
    className?: string;
} & PropsWithChildren;

function Row({ children, className, borderTop }: RowProps) {
    return (
        <div
            className={cn(
                'flex min-h-[22px] items-center justify-between gap-2 text-right',
                borderTop ? 'border-t border-neutral-200 pt-0.5' : '',
                className,
            )}
        >
            {children}
        </div>
    );
}

export const CellType = {
    NORMAL: 'NORMAL',
    LABEL: 'LABEL',
    DELTA: 'DELTA',
} as const;

export type CellType = (typeof CellType)[keyof typeof CellType];

type CellProps = {
    className?: string;
    cellType?: CellType;
    isPositive?: boolean | null;
} & PropsWithChildren;

function Cell({ children, className, cellType = CellType.NORMAL, isPositive }: CellProps) {
    if (cellType === CellType.NORMAL) {
        return <div className={className}>{children}</div>;
    } else if (cellType === CellType.LABEL) {
        return <div className={cn('flex-1 text-left text-neutral-500', className)}>{children}</div>;
    } else {
        return (
            <div
                className={cn(
                    'flex items-center justify-end text-sm font-semibold',
                    getCssClassForUpDownEqual(isPositive),
                    className,
                )}
            >
                {children}
            </div>
        );
    }
}

DetailedChartTooltip.Row = Row;
DetailedChartTooltip.Cell = Cell;
